/**
 * Parses platform filter updates embedded in chat SSE / assistant text.
 * The chat API mirrors the request prefix shape: Filters: {"datasource","dateRange",...}
 */

const parseFlexibleDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const parseDepthValue = (value) => {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'number' && !Number.isNaN(value)) return value;

  const trimmed = String(value).trim().toLowerCase();
  if (trimmed === 'surface' || trimmed === 'surface level') return 0;

  const match = trimmed.match(/-?\d+(\.\d+)?/);
  if (!match) return null;

  const parsed = Number(match[0]);
  return Number.isNaN(parsed) ? null : parsed;
};

export const normalizeDepthSelection = (depth, availableDepths = []) => {
  const numeric = parseDepthValue(depth);
  if (numeric === null) return null;

  if (!availableDepths.length) return numeric;

  const exact = availableDepths.find((d) => Number(d) === numeric);
  if (exact !== undefined) return exact;

  return availableDepths.reduce((closest, candidate) => (
    Math.abs(Number(candidate) - numeric) < Math.abs(Number(closest) - numeric)
      ? candidate
      : closest
  ), availableDepths[0]);
};

export const extractFiltersFromText = (text) => {
  if (!text || typeof text !== 'string') return null;

  const marker = 'Filters:';
  const idx = text.indexOf(marker);
  if (idx === -1) return null;

  let pos = idx + marker.length;
  while (pos < text.length && /\s/.test(text[pos])) pos += 1;
  if (text[pos] !== '{') return null;

  let depth = 0;
  const start = pos;
  for (let i = pos; i < text.length; i += 1) {
    if (text[i] === '{') depth += 1;
    else if (text[i] === '}') {
      depth -= 1;
      if (depth === 0) {
        try {
          return JSON.parse(text.slice(start, i + 1));
        } catch {
          return null;
        }
      }
    }
  }

  return null;
};

export const parseDateRangeValue = (dateRange) => {
  if (!dateRange) return null;

  if (typeof dateRange === 'object') {
    const startDate = parseFlexibleDate(dateRange.start || dateRange.startDate);
    const endDate = parseFlexibleDate(dateRange.end || dateRange.endDate);
    if (startDate && endDate) return { startDate, endDate };
    return null;
  }

  if (typeof dateRange !== 'string') return null;

  const parts = dateRange.split(/\s+to\s+/i);
  if (parts.length !== 2) return null;

  const startDate = parseFlexibleDate(parts[0].trim());
  const endDate = parseFlexibleDate(parts[1].trim());
  if (!startDate || !endDate) return null;

  return { startDate, endDate };
};

export const platformFiltersToSettings = (filters) => {
  if (!filters || typeof filters !== 'object') return null;

  const settings = {};

  if (filters.datasource) settings.area = filters.datasource;
  if (filters.model) settings.model = filters.model;

  const rawDepth = filters.depth ?? filters.selectedDepth ?? filters.depth_m ?? filters.depthMeters;
  if (rawDepth !== undefined && rawDepth !== null) {
    settings.depth = parseDepthValue(rawDepth);
  }

  if (filters.startDate && filters.endDate) {
    settings.startDate = parseFlexibleDate(filters.startDate);
    settings.endDate = parseFlexibleDate(filters.endDate);
  } else if (filters.dateRange) {
    const range = parseDateRangeValue(filters.dateRange);
    if (range) {
      settings.startDate = range.startDate;
      settings.endDate = range.endDate;
    }
  }

  const hasDateRange = settings.startDate && settings.endDate;
  const hasArea = Boolean(settings.area);
  const hasModel = settings.model != null;
  const hasDepth = settings.depth !== undefined && !Number.isNaN(settings.depth);

  if (!hasDateRange && !hasArea && !hasModel && !hasDepth) {
    return null;
  }

  return settings;
};

const collectEventText = (data, parts = []) => {
  if (!data || typeof data !== 'object') return parts;

  if (typeof data.output === 'string') parts.push(data.output);
  if (typeof data.text === 'string') parts.push(data.text);
  if (typeof data.message === 'string') parts.push(data.message);
  if (typeof data.result === 'string') parts.push(data.result);

  if (Array.isArray(data.content)) {
    for (const part of data.content) {
      if (!part) continue;
      if (typeof part.text === 'string') parts.push(part.text);
      else if (part.text?.value) parts.push(part.text.value);
    }
  }

  return parts;
};

const settingsFromStructuredPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;

  if (payload.filters) {
    return platformFiltersToSettings(payload.filters);
  }

  if (payload.action === 'set_filters' || payload.action === 'update_filters') {
    return platformFiltersToSettings(payload);
  }

  return platformFiltersToSettings(payload);
};

export const extractPlatformSettingsFromText = (text) => {
  if (!text || typeof text !== 'string') return null;

  const fromFilters = platformFiltersToSettings(extractFiltersFromText(text));
  if (fromFilters) return fromFilters;

  const trimmed = text.trim();
  if (!trimmed.startsWith('{')) return null;

  try {
    return settingsFromStructuredPayload(JSON.parse(trimmed));
  } catch {
    return null;
  }
};

export const extractPlatformSettingsFromSSEEvent = (data) => {
  const chunks = collectEventText(data, []);
  for (const chunk of chunks) {
    const settings = extractPlatformSettingsFromText(chunk);
    if (settings) return settings;
  }
  return null;
};

/**
 * Parses straightforward filter-change requests from the user's message.
 * Used as a local fallback when the chat API returns an empty stream.
 */
export const parseUserFilterIntent = (message) => {
  if (!message || typeof message !== 'string') return null;

  const settings = {};
  const text = message.trim();

  const depthPatterns = [
    /(?:change|set|switch|update)\s+(?:the\s+)?depth\s+(?:to\s+)?(\d+(?:\.\d+)?)\s*(?:m(?:eters?)?)?/i,
    /depth\s+(?:to|of|at)\s+(\d+(?:\.\d+)?)\s*(?:m(?:eters?)?)?/i,
    /(\d+(?:\.\d+)?)\s*(?:m(?:eters?)?)\s+depth/i
  ];

  for (const pattern of depthPatterns) {
    const match = text.match(pattern);
    if (match) {
      const depth = parseDepthValue(match[1]);
      if (depth !== null) {
        settings.depth = depth;
        break;
      }
    }
  }

  if (Object.keys(settings).length === 0) return null;
  return platformFiltersToSettings(settings);
};

export const buildPlatformActionConfirmation = (settings) => {
  if (!settings) return null;

  const parts = [];
  if (settings.depth !== undefined && settings.depth !== null) {
    parts.push(`depth to ${settings.depth} m`);
  }
  if (settings.area) {
    parts.push(`datasource to ${settings.area}`);
  }
  if (settings.startDate && settings.endDate) {
    parts.push('the date range');
  }

  if (parts.length === 0) return "I've updated the control panel filters.";
  return `I've updated the control panel: ${parts.join(', ')}.`;
};
